import { createSlice } from "@reduxjs/toolkit";
import qualifyQuestionData from "../../contanst/question.json";

// Utility Functions
const safeGetFromStorage = (storage, key, parse = false) => {
  try {
    const value = storage.getItem(key);
    return parse ? JSON.parse(value) : value;
  } catch {
    return parse ? null : null;
  }
};

const safeSetToStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // console.warn(`Failed to set key "${key}" in storage.`);
  }
};

const safeRemoveFromStorage = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // console.warn(`Failed to remove key "${key}" from storage.`);
  }
};

// Initial State
const initialState = {
  statusLoading: false,
  accessToken: safeGetFromStorage(sessionStorage, "accessToken"),
  refreshToken: safeGetFromStorage(sessionStorage, "refreshToken"),
  userData: safeGetFromStorage(localStorage, "userData", true),
  imageTypesData: [],
  objectivesDataRedux: [],
  objectivesDataReduxManualInput: [],
  questionsDataOndemandCustom: [],
  questionsDataOndemandDefault: [],
  questionsDataOndemandAIGen: [],
  segmentList: [],
  locationList: [],
  qualifyQuestion: qualifyQuestionData,
  description: "",
  requirement: "",
  expiryDate: "",
  startDate: "",
  thumbnails: [],
  questName: "",
  objectiveNameRedux: "",
  objectiveIdDefaultRedux: "",
  incentive: null,
};

// Redux Slice
export const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    // Authentication & User Data
    setStatusLoading: (state, action) => {
      state.statusLoading = action.payload;
    },
    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
      if (action.payload) {
        safeSetToStorage(sessionStorage, "accessToken", action.payload);
      } else {
        safeRemoveFromStorage(sessionStorage, "accessToken");
      }
    },
    setRefreshToken: (state, action) => {
      state.refreshToken = action.payload;
      if (action.payload) {
        safeSetToStorage(sessionStorage, "refreshToken", action.payload);
      } else {
        safeRemoveFromStorage(sessionStorage, "refreshToken");
      }
    },
    setUserData: (state, action) => {
      state.userData = action.payload;
      if (action.payload) {
        safeSetToStorage(
          localStorage,
          "userData",
          JSON.stringify(action.payload)
        );
      } else {
        safeRemoveFromStorage(localStorage, "userData");
      }
    },
    clearAccessToken: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.userData = null;
      safeRemoveFromStorage(sessionStorage, "accessToken");
      safeRemoveFromStorage(sessionStorage, "refreshToken");
      safeRemoveFromStorage(localStorage, "userData");
    },

    // Image Types Management
    setImageTypesData: (state, action) => {
      state.imageTypesData = action.payload || [];
    },
    clearImageTypesData: (state) => {
      state.imageTypesData = [];
    },

    //Objective Management
    setObjectivesDataRedux: (state, action) => {
      state.objectivesDataRedux = action.payload;
    },
    setObjectivesDataReduxManualInput: (state, action) => {
      state.objectivesDataReduxManualInput = action.payload;
    },
    updateObjectiveManual: (state, action) => {
      const updatedObjective = action.payload;
      const index = state.objectivesDataReduxManualInput.findIndex(
        (obj) => obj.id === updatedObjective.id
      );
      if (index !== -1) {
        state.objectivesDataReduxManualInput[index] = updatedObjective;
      }
    },
    addObjectiveManual: (state, action) => {
      state.objectivesDataReduxManualInput = [
        ...state.objectivesDataReduxManualInput,
        action.payload, // Thêm objective mới vào mảng
      ];
    },
    // clearobjectivesDataRedux: (state) => {
    //   state.objectivesDataRedux = [];
    // },

    // Questions Management
    setQuestionsDataOndemandDefault: (state, action) => {
      state.questionsDataOndemandDefault = action.payload;
    },
    setQuestionsDataOndemandAIGen: (state, action) => {
      state.questionsDataOndemandAIGen = action.payload;
    },
    updateQuestionCustom: (state, action) => {
      const newQuestion = action.payload;

      const existingIndex = state.questionsDataOndemandCustom.findIndex(
        (q) => (q.id || q.questionId) === newQuestion.questionId
      );

      if (existingIndex !== -1) {
        state.questionsDataOndemandCustom =
          state.questionsDataOndemandCustom.map((q, index) =>
            index === existingIndex ? { ...newQuestion } : q
          );
      } else {
        state.questionsDataOndemandCustom.push(newQuestion);
      }
    },
    updateQuestionAI: (state, action) => {
      const newQuestion = action.payload;

      const existingIndex = state.questionsDataOndemandAIGen.findIndex(
        (q) => (q.id || q.questionId) === newQuestion.questionId
      );

      if (existingIndex !== -1) {
        state.questionsDataOndemandAIGen = state.questionsDataOndemandAIGen.map(
          (q, index) => (index === existingIndex ? { ...newQuestion } : q)
        );
      } else {
        state.questionsDataOndemandAIGen = newQuestion;
      }
    },

    updateQuestionDefault: (state, action) => {
      const newQuestion = action.payload;

      const existingIndex = state.questionsDataOndemandDefault.findIndex(
        (q) => (q.id || q.questionId) === newQuestion.questionId
      );

      if (existingIndex !== -1) {
        state.questionsDataOndemandDefault =
          state.questionsDataOndemandDefault.map((q, index) =>
            index === existingIndex ? { ...newQuestion } : q
          );
      } else {
        state.questionsDataOndemandDefault.push(newQuestion);
      }
    },

    clearQuestionsDataOndemand: (state) => {
      state.questionsDataOndemandCustom = [];
    },
    clearQuestionsDataOndemandDefault: (state) => {
      state.questionsDataOndemandDefault = [];
    },
    deleteQuestion: (state, action) => {
      const questionId = action.payload;

      if (Array.isArray(state.questionsDataOndemandCustom)) {
        state.questionsDataOndemandCustom =
          state.questionsDataOndemandCustom.filter(
            (q) => (q.questionId || q.id) !== questionId
          );
      } else {
        console.warn(
          "questionsDataOndemandCustom is not an array:",
          state.questionsDataOndemandCustom
        );
      }
    },
    deleteQuestionDefault: (state, action) => {
      const questionId = action.payload;
      if (Array.isArray(state.questionsDataOndemandDefault)) {
        state.questionsDataOndemandDefault =
          state.questionsDataOndemandDefault.filter(
            (q) => (q.questionId || q.id) !== questionId
          );
      } else {
        console.warn(
          "questionsDataOndemandDefault is not an array:",
          state.questionsDataOndemandDefault
        );
      }
    },
    deleteQuestionAI: (state, action) => {
      const questionId = action.payload;

      if (Array.isArray(state.questionsDataOndemandAIGen)) {
        state.questionsDataOndemandAIGen =
          state.questionsDataOndemandAIGen.filter(
            (q) => (q.questionId || q.id) !== questionId
          );
      } else {
        console.warn(
          "questionsDataOndemandAIGen is not an array:",
          state.questionsDataOndemandAIGen
        );
      }
    },
    // Segment Management
    setSegments: (state, action) => {
      state.segmentList = action.payload;
    },
    removeSegment: (state, action) => {
      state.segmentList = state.segmentList.filter(
        (segment) => segment.id !== action.payload
      );
    },
    clearSegments: (state) => {
      state.segmentList = [];
    },

    // Location Management
    setLocations: (state, action) => {
      state.locationList = action.payload;
    },
    removeLocation: (state, action) => {
      state.locationList = state.locationList.filter(
        (loc) => loc.id !== action.payload
      );
    },
    addLocation: (state, action) => {
      state.locationList.push(action.payload);
    },
    clearLocations: (state) => {
      state.locationList = [];
    },

    // Qualify Questions Management
    setQualifyQuestions: (state, action) => {
      state.qualifyQuestion = action.payload;
    },
    addQualifyQuestion: (state, action) => {
      state.qualifyQuestion = [...state.qualifyQuestion, action.payload];
    },
    addOrUpdateQualifyQuestion: (state, action) => {
      const newQuestion = action.payload;
      const existingIndex = state.qualifyQuestion.findIndex(
        (q) => q.id === newQuestion.id
      );
      if (existingIndex !== -1) {
        state.qualifyQuestion[existingIndex] = {
          ...state.qualifyQuestion[existingIndex],
          ...newQuestion,
          completed: true,
        };
        const nextQuestionIndex = state.qualifyQuestion.findIndex(
          (q) => parseInt(q.id) === parseInt(newQuestion.id) + 1
        );
        if (nextQuestionIndex !== -1) {
          state.qualifyQuestion[nextQuestionIndex] = {
            ...state.qualifyQuestion[nextQuestionIndex],
            locked: false,
          };
        }
      } else {
        state.qualifyQuestion.push(newQuestion);
      }
    },
    removeQualifyQuestion: (state, action) => {
      state.qualifyQuestion = state.qualifyQuestion.filter(
        (question) => question.id !== action.payload
      );
    },
    clearQualifyQuestions: (state) => {
      state.qualifyQuestion = [];
    },

    // Form Data Management
    setDescription: (state, action) => {
      state.description = action.payload;
    },
    setRequirement: (state, action) => {
      state.requirement = action.payload;
    },
    setExpiryDate: (state, action) => {
      state.expiryDate = action.payload;
    },
    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    clearDescription: (state) => {
      state.description = initialState.description;
    },
    clearRequirement: (state) => {
      state.requirement = initialState.requirement;
    },
    clearExpiryDate: (state) => {
      state.expiryDate = initialState.expiryDate;
    },
    clearStartDate: (state) => {
      state.startDate = initialState.startDate;
    },
    // Thumbnails Management
    addThumbnail: (state, action) => {
      state.thumbnails = [...state.thumbnails, action.payload];
    },
    setThumbnails: (state, action) => {
      state.thumbnails = action.payload;
    },
    clearThumbnails: (state) => {
      state.thumbnails = [];
    },

    // Quest Name Management
    setQuestName: (state, action) => {
      state.questName = action.payload;
    },
    setObjectiveNameRedux(state, action) {
      state.objectiveNameRedux = action.payload;
      state.questName = action.payload;
    },

    setObjectiveDefaultIdRedux(state, action) {
      state.objectiveIdDefaultRedux = action.payload;
    },

    // Clear All Form Data
    clearAllFormData: (state) => {
      // state.objectivesDataRedux = [];
      // state.objectivesDataReduxManualInput = [];
      state.questionsDataOndemandCustom = [];
      state.questionsDataOndemandDefault = [];
      state.questionsDataOndemandAIGen = [];
      state.segmentList = [];
      state.locationList = [];
      state.description = initialState.description;
      state.requirement = initialState.requirement;
      state.expiryDate = initialState.expiryDate;
      state.startDate = initialState.startDate;
      state.thumbnails = [];
      state.questName = "";
      state.objectiveNameRedux = "";
      state.objectiveIdDefaultRedux = "";
      state.qualifyQuestion = qualifyQuestionData;
    },
    clearObjective: (state) => {
      state.objectivesDataRedux = [];
      state.objectivesDataReduxManualInput = [];
    },
    clearQuestionAIGen: (state) => {
      state.questionsDataOndemandAIGen = [];
    },
    clearQuestionCustom: (state) => {
      state.questionsDataOndemandCustom = [];
    },

    //Incentive
    setIncentiveRedux: (state, action) => {
      state.incentive = action.payload;
    },
  },
});

// Export Actions
export const {
  setStatusLoading,
  setAccessToken,
  setRefreshToken,
  setUserData,
  clearAccessToken,
  setImageTypesData,
  clearImageTypesData,
  setObjectivesDataRedux,
  setObjectivesDataReduxManualInput,
  addObjectiveManual,
  setQuestionsDataOndemandDefault,
  setQuestionsDataOndemandAIGen,
  updateQuestionCustom,
  updateQuestionDefault,
  updateQuestionAI,
  clearQuestionsDataOndemand,
  clearQuestionsDataOndemandDefault,
  deleteQuestion,
  deleteQuestionDefault,
  deleteQuestionAI,
  setSegments,
  removeSegment,
  clearSegments,
  setLocations,
  removeLocation,
  addLocation,
  clearLocations,
  setQualifyQuestions,
  addQualifyQuestion,
  addOrUpdateQualifyQuestion,
  removeQualifyQuestion,
  clearQualifyQuestions,
  setDescription,
  setRequirement,
  setExpiryDate,
  setStartDate,
  clearDescription,
  clearRequirement,
  clearExpiryDate,
  clearStartDate,
  addThumbnail,
  setThumbnails,
  clearThumbnails,
  setQuestName,
  clearAllFormData,
  clearObjective,
  clearQuestionAIGen,
  clearQuestionCustom,
  setObjectiveNameRedux,
  setObjectiveDefaultIdRedux,
  updateObjectiveManual,
  setIncentiveRedux,
} = commonSlice.actions;

export default commonSlice.reducer;
